import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Configurator as PulseConfigurator, event as pulseEvent } from '@blocket/tracking-pulse'
import { Configurator as GTMConfigurator } from '@blocket/tracking-gtm'
import BlazarTrackingConnector from '@blocket/tracking-blazar-connector'
import { AccountVerificationFeedback, LoginAlert } from '@blocket/authentication-fe'
import { BaseCss } from '@blocket/blocket-ui'
import {
  removeConnectProperties,
  clearRecipientMemberBankidVerifiedChannelUrls,
} from 'reduxBits/chat/actions'
import { clearCarouselImages } from 'reduxBits/ui/actions'
import { setBaseUrl } from 'reduxBits/config/actions'
import ScreenSizeWatcher from 'plugins/device/ScreenSizeWatcher'
import AdWatchesCountPoller from 'plugins/adWatchesCount'
import snakeCaseKeys from 'utils/snakeCase'
import useOnMount from 'customHooks/useOnMount'
import ImageCarousel from 'components/sharedComponents/ImageCarousel'
import ErrorBoundary from './ErrorBoundary'
import SendbirdConnector from './SendbirdConnector'
import Head from './Head'
import AdBlockDetector from './AdBlockDetector'
import OptimizelyProvider from './Optimizely'
import { setupAndShowCookieConsentModal, subscribeCookieConsent } from 'utils/vendCookieConsent'
import { addGtmConsentListener } from 'utils/vendCookieConsent/gtmConsent'
import pulse from '@blocket/tracking-pulse'

const Root = ({ children }) => {
  const [
    enableShowOfAccountVerificationFeedback,
    setEnableShowOfAccountVerificationFeedback,
  ] = useState(false)

  const loggedIn = useSelector((state) => state.authentication.loggedIn)
  const userId = useSelector((state) => state.user.id)
  const { images, activeIndex } = useSelector((state) => state.ui.carousel)
  const accountVerifiedByBankid = useSelector(
    (state) => state.account.verification.status?.account?.verifiedByBankid,
  )
  const bankIdVerificationEnabled = useSelector(
    (state) => state.optimizely.features.bankIdVerification?.enabled,
  )
  const bearerToken = useSelector((state) => state.authentication?.apiKey?.clientSecret)
  const credentials = useSelector((state) => snakeCaseKeys(state.authentication?.apiKey))
  const dispatch = useDispatch()

  let hasPulseActor

  useOnMount(() => {
    addGtmConsentListener()
    setupAndShowCookieConsentModal(userId)
    subscribeCookieConsent()

    if (!hasPulseActor) {
      pulse.update({ actor: { id: userId, realm: 'schibsted.com' } })
      hasPulseActor = true
    }
  })

  useOnMount(() => {
    dispatch(setBaseUrl(window.location.origin))
  })

  useEffect(() => {
    if (!loggedIn) {
      // removes chat connect credentials from local storage
      dispatch(removeConnectProperties())
      dispatch(clearRecipientMemberBankidVerifiedChannelUrls())
    }
  }, [loggedIn, dispatch])

  useEffect(() => {
    if (loggedIn) {
      setEnableShowOfAccountVerificationFeedback(bankIdVerificationEnabled)
    }
  }, [bankIdVerificationEnabled, loggedIn])

  return (
    <ErrorBoundary>
      <Head />
      <AdBlockDetector />
      <PulseConfigurator
        blocketApiUrl={process.env.REGRESS_ENV ? state.config?.urls?.api : 'https://api.blocket.se'}
        userId={userId}
        providerId="blocket"
        author=""
        environment="production"
        applicationName="blocket-web-chirp"
        applicationVersion="1.0.0"
      />
      <GTMConfigurator
        blocketApiUrl="https://api.blocket.se"
        bearerToken={bearerToken}
        credentials={credentials}
      />
      <BaseCss boxSizing="border-box" />
      <ScreenSizeWatcher />
      <AdWatchesCountPoller />
      <BlazarTrackingConnector />{' '}
      {/* makes it easier to set actor id on all tracking events for logged in users, can be replaced by pulse.update({ actor: { id: userId }}) in the future */}
      <LoginAlert vendBranding />
      {loggedIn && <SendbirdConnector />}
      {/* the rest of the logic for when to show this is handled by AccountVerificationFeedback component in 'authentication-fe' */}
      {enableShowOfAccountVerificationFeedback && (
        <AccountVerificationFeedback
          accountVerified={accountVerifiedByBankid}
          pulseEvent={pulseEvent}
        />
      )}
      {/* See ImageCarousel README */}
      {Boolean(images?.length) && (
        <ImageCarousel
          images={images}
          scrollToIndex={activeIndex}
          onClose={() => dispatch(clearCarouselImages())}
        />
      )}
      <OptimizelyProvider>{children}</OptimizelyProvider>
    </ErrorBoundary>
  )
}

Root.propTypes = {
  children: PropTypes.node,
}

export default Root
